'use client';

import { Box, Grid, Accordion, AccordionSummary, AccordionDetails, Typography, Button } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRouter } from "@/i18n/routing";

import { useTranslations } from "next-intl";

const FaqSection = () => {
    const t = useTranslations('HomePage.faq');

    const router = useRouter();

    const onGotoSection = (section: string) => {
        router.push(`/service/${section}`);
    };

    const faqs = [
        {
            question: "q_1",
            answer: [
                "a_1_1",
                "a_1_2",
                "a_1_3"
            ]
        },
        {
            question: "q_2",
            answer: [
                "a_2_1",
                "a_2_2"
            ]
        },
        {
            question: "q_3",
            answer: [
                "a_3_1",
                "a_3_2",
                "a_3_3"
            ]
        },
    ];

    return (
        <Box
            sx={{
                py: 6,
                backgroundColor: "#f8fafc",
            }}
        >
            <Grid container sx={{ maxWidth: "lg", margin: "auto", px: { xs: 2.5, md: 0 } }}>
                <Grid size={12} sx={{ textAlign: "center" }}>
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                        <span className="inline-block bg-[#F15859] text-white text-xs font-bold px-6 py-1.5 rounded-full mb-6 shadow-sm">
                            {t(`chip`)}
                        </span>
                        <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: "#132c4b", mb: 2 }}>
                            {t(`title`)}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#64748b", maxWidth: "90%", margin: "auto" }}>
                            {t(`description`)}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 10 }} sx={{ margin: "auto", mb: 6 }}>
                    {faqs.map((faq, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                mb: 2,
                                borderRadius: '8px !important',
                                '&:before': { display: 'none' }, // Remove default divider
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#132c4b' }} />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                            >
                                <Typography sx={{ fontWeight: 'bold', color: '#132c4b' }}>
                                    {t(faq.question)}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                                    {faq.answer.map((line, i) => (
                                        <Typography key={i} sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                                            {t(line)}
                                        </Typography>
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Grid>
                <Grid size={{ xs: 12, md: 10 }} sx={{ margin: "auto", textAlign: "center" }}>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: "#132c4b", mb: 2 }}>
                        {t(`title_2`)}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#64748b", maxWidth: "90%", margin: "auto", mb: 4 }}>
                        {t(`description_2`)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: '#F15859',
                                fontWeight: 'bold',
                                px: 6,
                                py: 1.5,
                                fontSize: '1rem',
                                borderRadius: '10px',
                                boxShadow: '0 4px 14px 0 rgba(225, 29, 72, 0.39)',
                                '&:hover': {
                                    backgroundColor: '#be123c'
                                }
                            }}
                            onClick={() => onGotoSection("#ex-plan-section")}
                        >
                            {t(`button_1`)}
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{
                                borderColor: '#132c4b',
                                color: '#132c4b',
                                fontWeight: 'bold',
                                px: 6,
                                py: 1.5,
                                fontSize: '1rem',
                                borderRadius: '10px',
                                '&:hover': {
                                    borderColor: '#132c4b',
                                    backgroundColor: 'rgba(19, 44, 75, 0.05)'
                                }
                            }}
                            onClick={() => onGotoSection("#plan-section")}
                        >
                            {t(`button_2`)}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqs.map(faq => ({
                            "@type": "Question",
                            "name": t(faq.question),
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": faq.answer.map(line => t(line)).join(" ")
                            }
                        }))
                    })
                }}
            />
        </Box >
    );
};

export default FaqSection;